import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, Link2Off, UserRound, Phone } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
const fmtPhone = (p) => (/^\+90\d{10}$/.test(p || "") ? `+90 ${p.slice(3, 6)} ${p.slice(6, 9)} ${p.slice(9, 11)} ${p.slice(11)}` : p);

const LinkState = ({ d, onUnlink }) => {
  if (d.linked_customer) {
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="yh-link-linked">
        <div className="text-sm"><Badge className="bg-emerald-600 hover:bg-emerald-600 text-white mr-2">Hesaba bağlı</Badge>
          {d.linked_customer.name} {d.linked_customer.surname || ""} · {d.linked_customer.email}</div>
        <Button size="sm" variant="ghost" onClick={onUnlink} data-testid="yh-link-remove"><Link2Off className="w-4 h-4 mr-1" />Bağlantıyı kaldır</Button>
      </div>
    );
  }
  if (d.link_request) {
    const r = d.link_request;
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="yh-link-pending">
        <div className="text-sm">
          <Badge variant={r.expired ? "destructive" : "secondary"} className="mr-2">{r.expired ? "Süresi doldu" : "Müşteri onayı bekleniyor"}</Badge>
          {r.customer?.email} · son geçerlilik {fmtDate(r.expires_at)}
        </div>
        <Button size="sm" variant="ghost" onClick={onUnlink} data-testid="yh-link-cancel">İsteği iptal et</Button>
      </div>
    );
  }
  return null;
};

export const YhCustomerPanel = ({ d, onChanged }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const snap = d.yh_customer_snapshot || {};
  const closed = ["CANCELLED", "COMPLETED"].includes(d.status);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setEmail(""); onChanged(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendLink = () => {
    if (!email.trim()) return toast.error("Müşterinin hesap e-postasını girin");
    run(() => api.post(`/admin/transport/listings/${d.id}/link-customer`, null, { params: { email: email.trim() } }), "Bağlama isteği müşteriye gönderildi");
  };
  const unlink = () => run(() => api.delete(`/admin/transport/listings/${d.id}/link-customer`), "Bağlantı/istek kaldırıldı");

  return (
    <div className="rounded-xl border p-3 space-y-3" data-testid="yh-customer-panel">
      <div className="flex items-center gap-4 flex-wrap text-sm">
        <span className="flex items-center gap-1"><UserRound className="w-4 h-4" /><b data-testid="yh-customer-name-view">{snap.name}</b></span>
        <a href={`tel:${snap.phone}`} className="flex items-center gap-1 underline" data-testid="yh-customer-phone-view"><Phone className="w-4 h-4" />{fmtPhone(snap.phone)}</a>
        <span className="text-xs text-muted-foreground">{d.yh_model === "model_3" ? "KVKK onayı sonrası yalnız onaylı sağlayıcılara gösterilir" : "Sağlayıcılara gösterilmez"}</span>
      </div>
      {d.yh_model === "model_2" && <LinkState d={d} onUnlink={unlink} />}
      {d.yh_model === "model_2" && !d.linked_customer && !d.link_request && !closed && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">İsteğe bağlı: müşterinin platform hesabı varsa e-postasıyla bağlama isteği gönderin. Müşteri kendi hesabından onaylayınca bağlanır ve işin durumunu takip edebilir.</div>
          <div className="flex gap-2">
            <Input type="email" placeholder="Müşteri e-postası" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="yh-link-email" />
            <Button onClick={sendLink} disabled={busy} variant="outline" data-testid="yh-link-send"><Link2 className="w-4 h-4 mr-1" />İstek Gönder</Button>
          </div>
        </div>
      )}
    </div>
  );
};
